import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/lib/api';
import { setKioskSession } from '@/lib/session';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/kiosk/activate')({
  validateSearch: (search: Record<string, unknown>) => ({
    secret: typeof search['secret'] === 'string' ? search['secret'] : '',
  }),
  component: KioskActivatePage,
});

function KioskActivatePage() {
  const { secret } = Route.useSearch();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'activating' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function activate() {
    if (!secret) return;
    setStatus('activating');
    setErrorMsg('');
    try {
      const res = await apiFetch<{
        accessToken: string;
        deviceLabel: string;
        property: { id: string; name: string; role: string; archivedAt: string | null; archivedReason: string | null };
      }>('/v1/kiosk/activate/' + secret, {
        method: 'POST',
        body: JSON.stringify({}),
        headers: { 'Content-Type': 'application/json' },
      });
      setKioskSession(res.accessToken, {
        deviceLabel: res.deviceLabel,
        property: { id: res.property.id, name: res.property.name },
      });
      void navigate({ to: '/kiosk' });
    } catch (err) {
      setStatus('error');
      if (err instanceof ApiError) {
        if (err.code === 'invalid_qr') {
          setErrorMsg('QR code not recognised. Ask the property owner to generate a new one.');
        } else if (err.code === 'property_archived') {
          setErrorMsg('This property is archived and cannot accept kiosk sessions.');
        } else {
          setErrorMsg('Activation failed. Please try again.');
        }
      } else {
        setErrorMsg('Activation failed. Please try again.');
      }
    }
  }

  useEffect(() => {
    if (secret) void activate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!secret) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">No activation code found. Scan the QR code on your property settings page.</p>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4 text-center max-w-sm px-4">
        {status === 'activating' && <p className="text-muted-foreground">Activating…</p>}
        {status === 'error' && (
          <>
            <p className="text-destructive">{errorMsg}</p>
            <Button onClick={() => void activate()}>Retry</Button>
          </>
        )}
      </div>
    </main>
  );
}
